import { API_BASE, AUTH_TOKEN } from '../config';
import { useWorldStore } from '../state/worldStore';
import { placeCounterItems } from '../state/counterItems';
import { placeFuel } from '../state/gymFuel';
import { openPrintStudio } from '../state/printStudio';
import { ROOMS } from '../rooms';

// worldJob — what a JOB did, applied to the room, from whichever surface it
// was talked to on.
//
// Khari, 2026-10-30: "Voice chat doesn't support jobs yet, so ordering is text
// only. — Please add it. If this is a new tool call, we should make it generic
// enough to handle more items like this. The music genius and the barista are
// the first two examples to use it."
//
// A job's tools (API WorldRoleService: search_tracks, show_on_wall,
// save_to_likes, take_order, ring_up…) write what they DID into one per-turn bag. Text
// gets the bag back on the counter's reply (POST /api/world/shop/talk); voice
// gets it back from each tool call (POST /api/world/role-tool). ⚠ BOTH apply it
// HERE, through one function, so the drink in the café and the records on the
// wall cannot come out differently depending on whether you typed or spoke —
// and a new tool's field is handled once, below, for both.

/**
 * Apply a job's bag to the World.
 *
 *   shelf  → the wall (null = he did not change it: leave it, so an identical
 *            shelf is not re-hung and every animated sleeve restarted)
 *   order  → the café's cup, ONLY in a café room (one with tables) — a drink
 *            they made, never one guessed from their words (CafeCounter
 *            carries it to the table or the bar)
 *   liked  → a note; the like itself was already saved server-side
 *   purchase → what the cashier rang up (API GroceryService ring_up, 2026-10-30:
 *            "a corner market with a job for an artist to work the cash
 *            register"), ONLY in a room with a `counter` spot — bagged and set on
 *            the counter for a minute (state/counterItems → CounterItems). Generic:
 *            any job that hands over a few named things lands them the same way.
 *   fuel   → a can the fitness instructor handed over (API GymService
 *            hand_fuel, 2026-10-31), ONLY in a room with a fuel bar (rooms.js
 *            `fuel`) — set on the bar until drunk (state/gymFuel → GymFloor);
 *            drinking it is the player's minute of speed and light (EnergyBoost)
 *   studio → the print shop's studio panel turned to a section (API
 *            PrintShopService show_studio, 2026-10-31), ONLY in a `printshop`
 *            room — state/printStudio; the clerk points, the customer makes
 *   (future fields go here)
 *
 * Returns `{ note }` — the quiet line the counter shows, or `null` when this
 * bag says nothing (so the caller leaves whatever note is up).
 */
export function applyRoleOut(out, { zone = null, roleKey = null } = {}) {
  if (!out || typeof out !== 'object') return { note: null };
  const st = useWorldStore.getState();
  const cafe = !!(zone && ROOMS[zone]?.tables?.length);
  let note = null;
  if (cafe && out.order?.drink) st.placeCafeOrder({ drink: out.order.drink, zone, roleKey });
  if (zone && ROOMS[zone]?.counter && out.purchase?.items?.length) {
    placeCounterItems({ items: out.purchase.items, zone, roleKey });
  }
  if (zone && ROOMS[zone]?.fuel && out.fuel?.drink) placeFuel({ zone, drink: out.fuel.drink, roleKey });
  if (zone && ROOMS[zone]?.printshop && out.studio?.section) openPrintStudio(zone, out.studio);
  if (out.shelf) {
    st.setShelfTracks(out.shelf);
    note = out.shelf.length ? `▸ ${out.shelf.length} on the wall` : '';
  }
  if (out.liked?.length) note = '♥ saved to your likes';
  return { note };
}

/**
 * Run one of a job's tools for the voice agent (its client tool call).
 *
 * Sends WHERE (zone), WHICH JOB (roleKey), the tool and its input — never who:
 * the server looks up who is on shift and what the job grants, and re-checks
 * every gate (a signed-out visitor cannot save a like however the agent asks).
 * `seat` is read at CALL time, so a customer who sat down mid-call has the drink
 * brought to the table they are at now.
 *
 * → { result: string for the agent, out: the bag | null }
 */
export async function runRoleTool({ zone, roleKey, tool, input = {}, seat = null, token = AUTH_TOKEN }) {
  const res = await fetch(`${API_BASE}/api/world/role-tool`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ zone, roleKey, tool, input: input || {}, ...(seat ? { seat } : {}) }),
  });
  const d = await res.json().catch(() => ({}));
  return { result: typeof d.result === 'string' ? d.result : '', out: d.out || null };
}
